/**
 * One actor, read off the map: where they stand, how much weight they carry
 * here, why we think so, who they are tied to, and the way to their dossier.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Route, Split, X } from 'lucide-react';
import { Avatar, Pill } from '../../../components/ui';
import type { StanceView } from '../../../types/issues';
import { CONFIDENCE_LABEL, SOURCE_HINT, SOURCE_LABEL, formatDate } from '../components/format';
import { InfluenceBar } from '../components/PositionMark';
import { StanceGlyph, StanceLadder, StancePill } from './PositionScale';
import { BlocInfo, MapGraphNode, Neighbour, blocLabel, categoryLabel, profilePath } from './mapModel';
import { isUnknown } from './position';

export const NodePanel: React.FC<{
  node: MapGraphNode;
  stance?: StanceView | null;
  bloc?: BlocInfo | null;
  neighbours: Neighbour[];
  onSelect: (id: string) => void;
  onClose: () => void;
  className?: string;
}> = ({ node, stance, bloc, neighbours, onSelect, onClose, className = '' }) => {
  const { subject, position, isBridge, isSwing, confidence, category, lastMoved } = node.node;
  const crossing = neighbours.filter((n) => n.crossesCamps);

  return (
    <div className={`flex min-h-0 flex-col ${className}`}>
      <div className="flex items-start gap-2.5 px-4 pb-3 pt-4">
        <Avatar name={subject.label} kind={subject.type === 'organization' ? 'organization' : 'stakeholder'} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] leading-tight text-ink">{subject.label}</p>
          <p className="mt-0.5 truncate text-[12px] text-neutral-500">
            {subject.type === 'organization' ? 'Organisation' : 'Person'}
            {category ? ` · ${categoryLabel(category)}` : ''}
            {bloc ? ` · ${blocLabel(bloc)}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="-mr-1 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="border-t border-neutral-200/70 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <StancePill position={position} />
            <StanceLadder position={position} />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className="w-16 flex-shrink-0 text-[12px] text-neutral-500">Influence</span>
            <InfluenceBar value={node.influence * 100} muted={!node.node.issueInfluence} className="flex-1" />
          </div>
          <p className="mt-1.5 text-[11.5px] text-neutral-400">
            {node.node.issueInfluence
              ? 'On this issue, not their standing in general.'
              : 'From the directory — no issue-specific figure recorded.'}
          </p>

          {(isBridge || isSwing) && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {isBridge && (
                <Pill tone="neutral" title="Connected to both camps — a message can travel through them">
                  <Route className="h-3 w-3" strokeWidth={1.75} />
                  Bridge
                </Pill>
              )}
              {isSwing && (
                <Pill tone="amber" title="Undecided or lightly held, with enough influence to matter">
                  <Split className="h-3 w-3" strokeWidth={1.75} />
                  Swing
                </Pill>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-neutral-200/70 px-4 py-3">
          <p className="mb-1.5 text-[12px] text-neutral-500">Why we think so</p>
          {stance?.rationale ? (
            <p className="text-[13px] leading-relaxed text-ink">{stance.rationale}</p>
          ) : (
            <p className="text-[13px] text-neutral-400">
              {isUnknown(position) ? 'Nothing on record — this is a blind spot, not a neutral.' : 'No rationale recorded.'}
            </p>
          )}
          {(stance || lastMoved || confidence) && (
            <p className="mt-2 text-[11.5px] text-neutral-400" title={stance?.source ? SOURCE_HINT[stance.source] : undefined}>
              {[
                stance?.source ? SOURCE_LABEL[stance.source] : null,
                confidence ? `${CONFIDENCE_LABEL[confidence]} confidence` : null,
                formatDate(stance?.asOf || lastMoved) ? `as of ${formatDate(stance?.asOf || lastMoved)}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
          {stance?.evidenceUrl && (
            <a
              href={stance.evidenceUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-[12px] text-primary-600 hover:text-primary-700"
            >
              Evidence
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </a>
          )}
        </div>

        <div className="border-t border-neutral-200/70 px-4 py-3">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[12px] text-neutral-500">Connections</p>
            <p className="text-[11.5px] text-neutral-400">
              {neighbours.length} {neighbours.length === 1 ? 'tie' : 'ties'}
              {crossing.length > 0 && ` · ${crossing.length} across camps`}
            </p>
          </div>
          {neighbours.length === 0 ? (
            <p className="text-[13px] text-neutral-400">No ties recorded — they stand alone on this map.</p>
          ) : (
            <ul className="-mx-1">
              {neighbours.slice(0, 12).map((neighbour) => (
                <li key={neighbour.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(neighbour.id)}
                    className="flex w-full items-center gap-2 rounded-xl px-1.5 py-1.5 text-left transition-colors hover:bg-neutral-50"
                  >
                    <StanceGlyph position={neighbour.node.node.position} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] leading-tight text-ink">{neighbour.node.label}</span>
                      <span className="block truncate text-[11.5px] text-neutral-500">
                        {neighbour.edge.label || neighbour.edge.kind}
                      </span>
                    </span>
                    {neighbour.crossesCamps && (
                      <span className="flex-shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10.5px] text-amber-700">
                        Across camps
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="border-t border-neutral-200/70 px-4 py-3">
        <Link
          to={profilePath(subject)}
          className="inline-flex items-center gap-1 text-[13px] text-primary-600 transition-colors hover:text-primary-700"
        >
          Open profile
          <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
        </Link>
      </div>
    </div>
  );
};
